import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { GitMerge, Search } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errorMessage } from "@/lib/api";
import { inp } from "@/lib/crm";

const mergeBtn = "inline-flex min-h-[44px] shrink-0 items-center gap-1.5 rounded-full px-3 text-[15px] font-medium text-[#0062CC] hover:bg-[#007AFF]/10";
const sub = (c) => [c.company, c.email, c.phone].filter(Boolean).join(" · ");

export function MergePicker({ c, onClose }) {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  useEffect(() => {
    const t = setTimeout(() => api.get("/customers", { params: { q } }).then((r) => setRows(r.data.filter((x) => x.id !== c.id).slice(0, 30))).catch((e) => toast.error(errorMessage(e))), 200);
    return () => clearTimeout(t);
  }, [q, c.id]);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="merge-picker" className="max-w-lg">
        <DialogHeader><DialogTitle className="text-[22px] font-bold">Merge {c.name} with…</DialogTitle>
          <DialogDescription>Pick the duplicate. You choose which details to keep on the next screen.</DialogDescription></DialogHeader>
        <label className="relative block"><Search className="absolute left-3 top-3 h-4 w-4 text-[#8E8E93]" />
          <input data-testid="merge-picker-search" autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customers" className={`${inp} pl-9`} /></label>
        <div className="max-h-80 divide-y divide-black/[0.08] overflow-y-auto rounded-[12px] bg-white">
          {rows.map((x, i) => (
            <button key={x.id} data-testid={`merge-picker-option-${i}`} onClick={() => nav(`/customers/${c.id}/merge?with=${x.id}`)} className="block w-full px-4 py-3 text-left hover:bg-black/[0.03]">
              <p className="text-[15px] font-medium text-black">{x.name}</p><p className="text-[13px] text-[#6D6D72]">{sub(x) || x.showroom_name}</p>
            </button>
          ))}
          {!rows.length && <p className="px-4 py-6 text-center text-[15px] text-[#6D6D72]">No other customers found.</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function PossibleDuplicates({ c }) {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  useEffect(() => { if (c.can_delete) api.get(`/customers/${c.id}/duplicates`).then((r) => setRows(r.data)).catch(() => {}); }, [c.id, c.can_delete]);
  if (!rows.length) return null;
  return (
    <section className="space-y-1.5" data-testid="customer-possible-duplicates">
      <p className="px-4 text-[13px] uppercase text-[#6D6D72]">Possible duplicates</p>
      <div className="overflow-hidden rounded-[12px] bg-white">
        {rows.map((x, i) => (
          <div key={x.id} data-testid={`customer-duplicate-${i}`} className="flex items-center justify-between gap-3 border-b border-black/[0.08] px-4 py-2.5 last:border-0">
            <div className="min-w-0"><p className="truncate text-[15px] font-medium text-black">{x.name}</p><p className="truncate text-[13px] text-[#6D6D72]">{x.reason}{sub(x) ? ` · ${sub(x)}` : ""}</p></div>
            <button data-testid={`customer-duplicate-merge-${i}`} onClick={() => nav(`/customers/${c.id}/merge?with=${x.id}`)} className={mergeBtn}><GitMerge className="h-4 w-4" /> Merge</button>
          </div>
        ))}
      </div>
    </section>
  );
}

export function DuplicatesDialog({ onClose }) {
  const nav = useNavigate();
  const [pairs, setPairs] = useState(null);
  useEffect(() => { api.get("/customers/duplicates").then((r) => setPairs(r.data)).catch((e) => toast.error(errorMessage(e))); }, []);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="duplicates-dialog" className="max-w-2xl">
        <DialogHeader><DialogTitle className="text-[22px] font-bold">Possible duplicates</DialogTitle>
          <DialogDescription>Customers with the same email or phone, or a very similar name.</DialogDescription></DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto rounded-[12px] bg-white">
          {pairs?.map((p, i) => (
            <div key={`${p.a.id}-${p.b.id}`} data-testid={`duplicate-pair-${i}`} className="flex items-center justify-between gap-3 border-b border-black/[0.08] px-4 py-3 last:border-0">
              <div className="min-w-0"><p className="truncate text-[15px] font-medium text-black">{p.a.name} <span className="text-[#8E8E93]">and</span> {p.b.name}</p>
                <p className="truncate text-[13px] text-[#6D6D72]">{p.reason}{p.a.email || p.a.phone ? ` · ${p.a.email || p.a.phone}` : ""}</p></div>
              <button data-testid={`duplicate-pair-merge-${i}`} onClick={() => nav(`/customers/${p.a.id}/merge?with=${p.b.id}`)} className={mergeBtn}><GitMerge className="h-4 w-4" /> Merge</button>
            </div>
          ))}
          {pairs && !pairs.length && <p data-testid="duplicates-empty" className="px-4 py-8 text-center text-[15px] text-[#6D6D72]">No possible duplicates found.</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
